import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { checkCircleIcon, truckIcon } from '@progress/kendo-svg-icons';
import { TrackingConstants } from '../../app-core/constants/common-constants';
import { OrderStatus, StockStatus } from '../../app-core/enums/common-enum';
import { CommonService } from '../../app-core/services/common.service';
import { LoaderService } from '../../app-core/services/loader.service';
@Component({
	selector: 'app-tracking-history',
	templateUrl: './tracking-history.component.html',
	styleUrl: './tracking-history.component.scss',
})
export class TrackingHistoryComponent implements OnInit {
	trackingConstants: typeof TrackingConstants = TrackingConstants;
	truck = truckIcon;
	checkCircle = checkCircleIcon;
	currentStep = 0;
	wismoDetails: any;
	deliveryItem: any;
	serviceItems: any[] = [];
	isUpdateBtnDisabled = false;
	isShowStepper = true;

	steps = [
		{ label: 'With the Retailer', text: '', isValid: true },
		{ label: 'With US', text: '', isValid: true },
		{ label: 'Out For Delivery', text: '', isValid: true },
		{ label: 'Successfully Delivered', text: '', isValid: true },
	];

	STATUS_MAPPING = [
		{ order: 'AWAITING_STOCK', stock: 'AWAITING_STOCK', step: 'With the Retailer', delivery_date: '' },
		{ order: 'BOOKED', stock: 'AWAITING_STOCK', step: 'With Us', delivery_date: '' }, //if not booked date
		{ order: 'ROUTE_PLANNING', stock: 'AWAITING_STOCK', step: 'With the Retailer', delivery_date: '' },
		{ order: 'ROUTE_PLANNING', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' },
		{ order: 'AWAIT_QUINTIQ_BOOK', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' }, //if booked date
		{ order: 'AWAIT_QUINTIQ_BOOK', stock: 'AWAITING_STOCK', step: 'With Us', delivery_date: '' }, //if booked date
		{ order: 'B4P', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' }, //if not booked date
		{ order: 'B4P', stock: 'AWAITING_STOCK', step: 'With the Retailer', delivery_date: '' }, //if not booked date
		{ order: 'PICKING', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' },
		{ order: 'PICKING', stock: 'AWAITING_STOCK', step: 'With Us', delivery_date: '' },
		{ order: 'PREADVICE_CREATED', stock: 'AWAITING_STOCK', step: 'With Us', delivery_date: '' },
		{ order: 'LOADED', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' },
		{ order: 'IN_PROGRESS', stock: 'STOCK_RECEIVED', step: 'Out For Delivery', delivery_date: '' },
		{ order: 'COLLECTED', stock: '', step: 'Successfully Delivered', delivery_date: '' },
		{ order: 'DELIVERED', stock: '', step: 'Successfully Delivered', delivery_date: '' },
		{ order: 'STOCK_RECEIVED', stock: 'STOCK_RECEIVED', step: 'With Us', delivery_date: '' },
		{ order: 'AUDIT_CLOSED', stock: '', step: 'Cancelled', delivery_date: '' },
		{ order: 'CANCELLED', stock: '', step: 'Cancelled', delivery_date: '' },
	];

	verticalsteps: any[] = [];

	order = {
		orderNumber: '',
		upi: '',
		postcode: '',
		requestedBy: '1',
	};
	errorMessage: any;

	constructor(
		private router: Router,
		private commonService: CommonService,
		private route: ActivatedRoute,
		private loaderService: LoaderService
	) {}

	ngOnInit() {
		this.route.queryParams.subscribe((params) => {
			this.order.upi = params['upi'];
			this.order.postcode = params['postcode'];
		});
		this.updateStepIcons();
		this.getOrderDetails();
	}

	private updateStepIcons(): void {
		this.steps = this.steps.map((step, index) => ({
			...step,
			svgIcon: index === this.currentStep ? this.truck : step.isValid ? this.checkCircle : this.truck,
		}));
	}

	onStepActivate(e: any) {
		e.preventDefault();
	}

	goBack() {
		this.router.navigate(['']);
	}

	onUpdateDetails() {
		this.router.navigate(['update-order-details'], {
			queryParams: { url: 'update-details', upi: this.order.upi, postcode: this.order.postcode },
		});
	}

	getOrderDetails() {
		this.loaderService.show();
		const payload = {
			orderNumber: this.order.orderNumber,
			upi: this.order.upi,
			postcode: this.order.postcode,
			requestedBy: this.order.requestedBy,
		};
		this.commonService.getWismoDetails(payload).subscribe({
			next: (res) => {
				this.wismoDetails = res?.arrowxl_wismo_response?.wismo_detail;
				const items = this.wismoDetails?.order_line_info?.order_line_info_item;
				this.deliveryItem = items.find((item: any) => item.line_type === 'Delivery');
				this.serviceItems = items.filter((item: any) => item.line_type === 'Service');

				const wismoInfo = this.wismoDetails?.order_hdr_wismo_info?.order_hdr_wismo_info_item;

				const order = this.wismoDetails?.order_status;
				const stock = this.wismoDetails?.stock_status;
				const deliveryDate = this.wismoDetails?.delivery_date;

				this.updateStepsBasedOnStatus(order, stock, deliveryDate);
				// Update icons accordingly
				this.currentStep = this.steps.reduce((lastIdx, step, idx) => (step.isValid ? idx : lastIdx), 0);
				this.updateStepIcons();

				this.verticalsteps = wismoInfo.map((item: any) => {
					const dateObj = new Date(item.event_date_time);
					return {
						date: dateObj.toLocaleDateString('en-GB', {
							day: '2-digit',
							month: 'short',
						}),
						time: dateObj.toLocaleTimeString('en-GB', {
							hour: '2-digit',
							minute: '2-digit',
						}),
						activity: item.event,
						location: this.wismoDetails?.depot_name || '',
					};
				});
				this.loaderService.hide();
			},
			error: (err) => {
				this.errorMessage = err || 'Something went wrong. Please try again later.';
				this.loaderService.hide();
			},
		});
	}

	updateStepsBasedOnStatus(order: string, stock: string, deliveryDate?: string) {
		// Find a matching record from STATUS_MAPPING (ignoring delivery_date)
		let match = this.STATUS_MAPPING.find((m) => m.order === order && m.stock === stock);

		// Special rule for AWAIT_QUINTIQ_BOOK + AWAITING_STOCK
		if (order === OrderStatus.AWAITQUINTIQBOOK && stock === StockStatus.AWAITINGSTOCK) {
			if (deliveryDate && deliveryDate.trim() !== '') {
				// delivery date present → With Us
				match = { order, stock, delivery_date: deliveryDate, step: 'With Us' };
			} else {
				// no delivery date → With the Retailer
				match = { order, stock, delivery_date: '', step: 'With the Retailer' };
			}
		}

		// Determine which step to show
		const step = match?.step || 'With the Retailer';

		// Reset all steps
		this.steps.forEach((s) => (s.isValid = false));

		// Activate steps based on matched step
		switch (step) {
			case 'With the Retailer':
				this.steps[0].isValid = true;
				break;

			case 'With Us':
				this.steps[0].isValid = true;
				this.steps[1].isValid = true;
				break;

			case 'Out For Delivery':
				this.steps[0].isValid = true;
				this.steps[1].isValid = true;
				this.steps[2].isValid = true;
				this.isUpdateBtnDisabled = true;
				break;

			case 'Successfully Delivered':
				this.steps[0].isValid = true;
				this.steps[1].isValid = true;
				this.steps[2].isValid = true;
				this.steps[3].isValid = true;
				this.isUpdateBtnDisabled = true;
				break;

			case 'Cancelled':
				this.isUpdateBtnDisabled = true;
				this.isShowStepper = false;
				break;

			default:
				this.steps[0].isValid = true;
				break;
		}
	}
}
